import React, {DragEvent, useState} from 'react';
import {Popup, Layout, Permissions, Icon, Separator, Activities, Flex, Video} from 'components';
import {useNamedSelector, map, config, capitalize, printSidebar, useList, useDrop, overEvery, useDragStart, openPopup} from 'helpers';
import {update} from 'store/sorting';
import {forbid} from 'store/session';
import {RouteComponentProps} from 'types';

const addLinks = {
  activities: '/activities/add/',
  habits: '/habits/',
  routines: '/routines/add/'
};

export function Sidebar({match: {params: {module}}}: RouteComponentProps<{module: string}>) {
  let heading = '';
  const [ q, setSearchQuery ] = useState('');
  const type = module.replace(/routines|habits/, 'recurring');

  const permissions = useNamedSelector('permissions.list');

  const all = useNamedSelector('activities.list', {
    module,
    filter: overEvery(
      useNamedSelector('settings.shareStatusFilter', {context: 'activities'}),
      useNamedSelector('activities.externalCalendarsFilter')
    )
  });

  const [ list, Last ] = useList(all, {
    search: { q, keys: ['name', 'note', 'tag'] },
    deps: [ module ],
  });

  const onDrop = (event: DragEvent, grabbed) => {
    update({
      all: map(all, 'id').filter(Boolean),
      container: event.currentTarget,
      grabbed,
      name: capitalize(module)
    });
  };

  return (
    <Layout.Sidebar {...useDrop(onDrop, grabbed => grabbed.type === 'activity')}>
      <Flex className={['secondary', 'middle']}>
        <Layout.Sidebar.Search value={q} setValue={setSearchQuery} />
        <Icon name="print" onClick={printSidebar} />
        <Video.Trigger src={config.videos[module]} />
        <Activities.SidebarPreferences module={module} isSidebar shareStatus />
        {module === 'routines'
          ? <Icon name="plus-large" onClick={permissions.canAdd[type] ? openPopup(CreateActivityMenu) : forbid} />
          : <Icon name="plus-large" to={permissions.canAdd[type] && addLinks[module]} onClick={permissions.canAdd[type] ? undefined : forbid} />
        }
      </Flex>
      <Layout.Sidebar.Scrollable>
        { list.map(activity => activity.heading !== undefined
          ? <Separator key={heading=activity.heading} heading={activity.heading} icon={activity.heading} />
          : <Activities.Card
              Checkbox={module == 'activities' && Activities.Checkbox}
              navLink={`/${module}/${activity.id}/`}
              id={activity.id}
              key={heading + activity.id}
              {...useDragStart({type: 'activity', id: activity.id})}
            />
        ) }
        <Last />
        { list.length == 0 &&
          <p className="text-center secondary">
            { q ? 'No match.' : `You have no ${module}.` }
          </p>
        }
        <Permissions.Alert module={type} />
      </Layout.Sidebar.Scrollable>
    </Layout.Sidebar>
  );
}

const CreateActivityMenu = () => (
  <Popup.Menu>
    <Popup.Menu.Item to="/routines/add/">
      Add Routine
    </Popup.Menu.Item>
    <Popup.Menu.Item to="/routines/add-daily-ritual/">
      Add Daily Ritual
    </Popup.Menu.Item>
  </Popup.Menu>
);
